import { z } from "zod";
import type {
	BatchSource,
	BatchStatus,
	KotStatus,
	ServiceRequestStatus,
	ServiceRequestType,
} from "./enums";

/**
 * Realtime events and the WebSocket protocol, shared by the API, the worker
 * and `packages/realtime-client`. See docs/architecture/realtime-notifications.md.
 *
 * An event carries IDs and small fields only (Postgres `NOTIFY` payloads are
 * limited to 8 KB). Clients treat it as a hint: they refetch through the API.
 */

export const EVENT_TYPES = [
	"batch.created",
	"batch.accepted",
	"batch.rejected",
	"order.updated",
	"order.closed",
	"kot.updated",
	"service.requested",
	"service.updated",
	"bill.finalized",
	"bill.voided",
	"payment.recorded",
	"payment.claimed",
	"menu.changed",
	"printjob.created",
	"printjob.updated",
	"table.updated",
	"restaurant.updated",
	"site.changed",
	"upi.changed",
	"ai.job.updated",
] as const;
export const eventTypeSchema = z.enum(EVENT_TYPES);
export type EventType = z.infer<typeof eventTypeSchema>;

/** The envelope every event travels in, over `NOTIFY` and the socket. */
export const realtimeEventSchema = z.object({
	/** The `outbox_events` ID, as a string. */
	id: z.string(),
	type: eventTypeSchema,
	restaurantId: z.uuid(),
	/** Set for events about a table: they also reach the diners at it. */
	tableId: z.uuid().optional(),
	/** ISO time the change happened. */
	at: z.iso.datetime(),
	/** IDs and small fields. Never names, phone numbers or amounts a diner should not see. */
	data: z.record(z.string(), z.unknown()),
});
export type RealtimeEvent = z.infer<typeof realtimeEventSchema>;

/** Who receives an event. See `EVENT_AUDIENCE`. */
export type EventAudience = {
	/** Staff of the restaurant: channel `r:<restaurantId>`. */
	staff: boolean;
	/** Diners at the event's table: channel `t:<tableId>`. Needs `tableId`. */
	table: boolean;
	/** Anyone with the restaurant's storefront open: channel `m:<restaurantId>`. */
	public: boolean;
	/** Platform admins in the admin app: channel `a:platform` (Phase 8). */
	admin?: boolean;
};

const staffOnly: EventAudience = { staff: true, table: false, public: false };
const staffAndTable: EventAudience = { staff: true, table: true, public: false };
const everyone: EventAudience = { staff: true, table: false, public: true };
const adminOnly: EventAudience = { staff: false, table: false, public: false, admin: true };

/** The routing table from realtime-notifications.md, Events. */
export const EVENT_AUDIENCE: Readonly<Record<EventType, EventAudience>> = {
	"batch.created": staffAndTable,
	"batch.accepted": staffAndTable,
	"batch.rejected": staffAndTable,
	"order.updated": staffAndTable,
	"order.closed": staffAndTable,
	"kot.updated": staffOnly,
	"service.requested": staffAndTable,
	"service.updated": staffAndTable,
	"bill.finalized": staffAndTable,
	"bill.voided": staffAndTable,
	"payment.recorded": staffAndTable,
	"payment.claimed": staffOnly,
	"menu.changed": everyone,
	"printjob.created": staffOnly,
	"printjob.updated": staffOnly,
	"table.updated": staffOnly,
	"restaurant.updated": everyone,
	"site.changed": staffOnly,
	"upi.changed": staffOnly,
	"ai.job.updated": adminOnly,
};

/**
 * The `data` of each event: IDs and small fields, never names, phone numbers
 * or amounts. Clients refetch what they show. Bill, payment and print job
 * events arrive with Phases 4 and 5.
 */
export type EventData = {
	/** A diner placed a batch (`status: pending`, or `accepted` with auto-accept), or staff added one. */
	"batch.created": {
		batchId: string;
		source: BatchSource;
		status: BatchStatus;
		/** Staff batches: the order it joined. */
		orderId?: string;
	};
	"batch.accepted": { batchId: string; orderId: string; removedItemIds: string[] };
	"batch.rejected": { batchId: string };
	/** Items added or cancelled, a batch joined, the table moved or another order merged in. */
	"order.updated": { orderId: string };
	/** Cancelled, merged into another order (`mergedIntoId`), or later paid. */
	"order.closed": { orderId: string; status: "cancelled" | "completed"; mergedIntoId?: string };
	/** A KOT was marked done or open again. */
	"kot.updated": { kotId: string; orderId: string; status: KotStatus };
	/** A new request, or one asked again after 60 seconds (`count` taps so far). */
	"service.requested": { requestId: string; type: ServiceRequestType; count: number };
	"service.updated": {
		requestId: string;
		type: ServiceRequestType;
		status: Exclude<ServiceRequestStatus, "open">;
	};
	"bill.finalized": { billId: string; orderId: string };
	"bill.voided": { billId: string; orderId: string };
	"payment.recorded": { paymentId: string; billId: string };
	"payment.claimed": { orderId: string; billId: string | null };
	/** Any committed catalog change. */
	"menu.changed": Record<string, never>;
	"printjob.created": { printJobId: string; printerId: string };
	"printjob.updated": { printJobId: string; printerId: string; status: string };
	/**
	 * A table was taken or freed by an order, or a table or area was edited
	 * in More > Tables: refetch the Tables tab.
	 */
	"table.updated": { tableId?: string; areaId?: string; orderId?: string };
	/** Profile, status or settings changed. */
	"restaurant.updated": Record<string, never>;
	/** The published site or its published flag changed (Phase 7): the storefront drops its cached site. */
	"site.changed": Record<string, never>;
	/**
	 * The UPI ID payments go to was changed in billing settings (P4-T13). The
	 * worker notifies the owners; open staff screens refetch billing.
	 */
	"upi.changed": { changedBy: string | null };
	/**
	 * An AI job was queued, made progress, finished or failed (P8-T05). Only
	 * on the bus (best effort, not the outbox): the admin app refetches the
	 * job, and polls while its socket is down.
	 */
	"ai.job.updated": {
		jobId: string;
		kind: "menu_import" | "item_image" | "site_generate" | "copy";
		status: "queued" | "running" | "succeeded" | "failed";
		done: number;
		total: number;
	};
};

/** Staff of a restaurant. */
export const staffChannel = (restaurantId: string) => `r:${restaurantId}`;
/** Diners at a table. */
export const tableChannel = (tableId: string) => `t:${tableId}`;
/** Anyone viewing the restaurant's storefront: menu and settings changes. */
export const publicChannel = (restaurantId: string) => `m:${restaurantId}`;
/** Platform admins in the admin app: AI job progress. */
export const ADMIN_CHANNEL = "a:platform";

/** The channels an event goes to. */
export function channelsFor(event: Pick<RealtimeEvent, "type" | "restaurantId" | "tableId">) {
	const audience = EVENT_AUDIENCE[event.type];
	const channels: string[] = [];
	if (audience.staff) channels.push(staffChannel(event.restaurantId));
	if (audience.table && event.tableId) channels.push(tableChannel(event.tableId));
	if (audience.public) channels.push(publicChannel(event.restaurantId));
	if (audience.admin) channels.push(ADMIN_CHANNEL);
	return channels;
}

// ---------------------------------------------------------------------------
// WebSocket protocol. One endpoint, `wss://<host>/api/ws`.
// ---------------------------------------------------------------------------

/** Server `ping` interval. A socket that misses two is closed. */
export const WS_PING_INTERVAL_MS = 25_000;

/** Staff: first message and every token refresh. */
export const wsAuthMessageSchema = z.object({
	type: z.literal("auth"),
	/** A Clerk session token. */
	token: z.string().min(1).max(8192),
	restaurantId: z.uuid(),
	/** An admin support-session token (staff web only). */
	supportToken: z.string().max(4096).optional(),
});
export type WsAuthMessage = z.infer<typeof wsAuthMessageSchema>;

/**
 * Platform admins in the admin app (Phase 8): the first message and every
 * token refresh. Joins `a:platform`.
 */
export const wsAdminAuthMessageSchema = z.object({
	type: z.literal("adminAuth"),
	token: z.string().min(1).max(8192),
});
export type WsAdminAuthMessage = z.infer<typeof wsAdminAuthMessageSchema>;

/** Messages a client sends. */
export const wsClientMessageSchema = z.discriminatedUnion("type", [
	wsAuthMessageSchema,
	wsAdminAuthMessageSchema,
	z.object({ type: z.literal("pong") }),
]);
export type WsClientMessage = z.infer<typeof wsClientMessageSchema>;

export const WS_ROLES = ["staff", "guest", "public", "admin"] as const;
export type WsRole = (typeof WS_ROLES)[number];

/** Messages the server sends. */
export type WsServerMessage =
	/**
	 * The socket is authorised and joined `channels`. For staff, `expiresAt` is
	 * when the Clerk token runs out: send a fresh `auth` before then.
	 */
	| { type: "ready"; role: WsRole; channels: string[]; expiresAt: string | null }
	| { type: "event"; event: RealtimeEvent }
	/** Answer with `{ type: "pong" }`. */
	| { type: "ping" }
	/** Followed by a close with the matching code. */
	| { type: "error"; code: WsErrorCode };

export const WS_ERROR_CODES = [
	"UNAUTHENTICATED",
	"FORBIDDEN",
	"NOT_FOUND",
	"TOO_MANY_SOCKETS",
	"BAD_MESSAGE",
] as const;
export type WsErrorCode = (typeof WS_ERROR_CODES)[number];

export { WS_CLOSE, WS_PATH } from "./ws-close";

// ---------------------------------------------------------------------------
// Guest sessions. See docs/architecture/auth-tenancy.md, Guest sessions, and
// storefront-sites.md, Guest session contract.
// ---------------------------------------------------------------------------

/** The diner's cookie, set by the storefront on `/t/<qrToken>`. */
export const GUEST_COOKIE_NAME = "lm_guest";

/** 12 hours. The cookie's `Max-Age` and the session's life from its last scan. */
export const GUEST_SESSION_TTL_SECONDS = 12 * 60 * 60;

/**
 * The `Set-Cookie` value for a guest session token: `HttpOnly`, `Secure`
 * (except on plain http in development), `SameSite=Lax`, host-only, path `/`.
 */
export function guestCookieHeader(
	token: string,
	options: { secure: boolean; maxAgeSeconds?: number },
): string {
	const parts = [
		`${GUEST_COOKIE_NAME}=${token}`,
		"Path=/",
		`Max-Age=${options.maxAgeSeconds ?? GUEST_SESSION_TTL_SECONDS}`,
		"HttpOnly",
		"SameSite=Lax",
	];
	if (options.secure) parts.push("Secure");
	return parts.join("; ");
}

/** A cookie's value from a `Cookie` header, or null. */
export function readCookie(header: string | null | undefined, name: string): string | null {
	if (!header) return null;
	for (const part of header.split(";")) {
		const eq = part.indexOf("=");
		if (eq === -1) continue;
		if (part.slice(0, eq).trim() === name) return part.slice(eq + 1).trim() || null;
	}
	return null;
}
